<template>
  <div>
    <empty-state
      v-if="!restaurantIds"
      icon="mdi-truck-delivery"
      :title="$t('select_restaurant')"
      :subtitle="$t('please_select_restaurant_to_view_data')"
    />

    <companies-delivery-table
      v-else
      :data="filteredData"
      :loading="isLoading"
      :search-query="searchQuery"
    />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import CompaniesDeliveryTable from "@/components/guest/CompaniesDeliveryTable.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import axios from "@/plugins/axios";

export default {
  name: "AdminCompaniesDeliveryTab",
  mixins: [hasPrinting, hasExcel],
  components: {
    EmptyState,
    CompaniesDeliveryTable,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    selectedStatus: Object,
    isActiveTab: Boolean,
    selectedRestaurants: Array,
    restaurantIds: String,
  },
  data() {
    return {
      data: [],
      isLoading: false,
    };
  },
  computed: {
    selectedRestaurant() {
      return this.selectedRestaurants && this.selectedRestaurants.length > 0
        ? this.selectedRestaurants[0]
        : null;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("company"), value: "company", sortable: false },
        { text: this.$t("address"), value: "address", sortable: false },
        { text: this.$t("phone_no"), value: "phone", sortable: false },
        {
          text: this.$t("delivered_orders"),
          value: "orders",
          sortable: false,
          align: "center",
        },
      ];
    },
    filteredData() {
      if (!this.selectedStatus || this.selectedStatus.id === 0) {
        return this.data;
      }
      return this.data.filter((item) => item.status === this.selectedStatus.id);
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (
        !isPrinting ||
        !this.isActiveTab ||
        !this.filteredData ||
        this.filteredData.length === 0
      ) {
        return;
      }

      this.printCompaniesTable();
    },
    isExcelDownloading(isDownloading) {
      if (
        !isDownloading ||
        !this.isActiveTab ||
        !this.filteredData ||
        this.filteredData.length === 0
      ) {
        return;
      }

      this.exportCompaniesExcel();
    },
    dateRange: {
      handler() {
        if (this.selectedRestaurant) {
          this.loadData();
        }
      },
    },
    selectedStatus() {
      // Local filtering, no need to reload
    },
    selectedRestaurant: {
      handler() {
        if (this.selectedRestaurant) {
          this.loadData();
        }
      },
    },
  },
  methods: {
    async loadData() {
      if (!this.selectedRestaurant || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          restaurant_id: this.selectedRestaurant.id,
          date: this.dateRange[0],
          date2: this.dateRange[1],
        };

        // TODO: Replace with actual API endpoint when available
        const response = await axios.get("/admin/companies-delivery", { params });
        this.data = response.data.data || response.data;
      } catch (error) {
        console.error("Error loading companies delivery data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    printCompaniesTable() {
      const tableItems = this.filteredData.map((item, index) => ({
        num: index + 1,
        company: item.company || "",
        address: item.address || "",
        phone: item.phone || "",
        orders: item.orders || 0,
      }));

      this.printDocument({
        pageTitle: this.$t("companies_delivery"),
        title: `${this.toLatin(this.selectedRestaurant?.name || "")} - ${this.$t(
          "companies_delivery"
        )}`,
        subtitle: `${this.$t("date")}: ${this.dateRange[0]} - ${this.dateRange[1]}${
          this.selectedStatus && this.selectedStatus.id !== 0
            ? ` | ${this.$t("status")}: ${this.$t(this.selectedStatus.name)}`
            : ""
        }`,
        tables: [
          {
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
    exportCompaniesExcel() {
      const tableItems = this.filteredData.map((item, index) => ({
        num: index + 1,
        company: item.company || "",
        address: item.address || "",
        phone: item.phone || "",
        orders: item.orders || 0,
      }));

      this.downloadExcel({
        fileName: `${this.toLatin(
          this.selectedRestaurant?.name || "admin"
        )}_firme_dostava_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("companies_delivery"),
            title: `${this.toLatin(this.selectedRestaurant?.name || "")} - ${this.$t(
              "companies_delivery"
            )}`,
            subtitle: `${this.$t("date")}: ${this.dateRange[0]} - ${this.dateRange[1]}${
              this.selectedStatus && this.selectedStatus.id !== 0
                ? ` | ${this.$t("status")}: ${this.$t(this.selectedStatus.name)}`
                : ""
            }`,
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
  },
};
</script>
